// A task's CTA is "Resolve with {agent}" — a handoff to the Maestro — until the task's own text says
// what the user is actually about to do. When it does, the button becomes that action instead, because
// sending someone to the Maestro to be told "open this link" is a detour around a link we already hold.
//
// Today there is exactly ONE recognized action: a browse session paused for a human. `Browse` pauses on
// the things only a person can clear (a login, a CAPTCHA, a payment), and the sub-agent is instructed to
// park its task with the live-view URL in the summary, verbatim — see the harness's
// `BrowseCallbacks` ("[BROWSE NEEDS YOU]") and `ManageTask` set_status ACTION_NEEDED.
//
// Shared 1:1 with iOS `TaskKnownAction.swift` and Android `TaskKnownAction.kt`.

export type TaskKnownAction = { kind: 'browseSession'; url: string };

/** The task text this reads, in the order it reads it. */
export interface TaskActionText {
  summary?: string;
  currentAction?: string;
  subtitle?: string;
}

// The live-view link is the session id on one of our own hosts:
//   https://symphony.wix.com/browse/<sessionId>   or   https://symphony.wix.app/browse/<sessionId>
//
// Two things this has to get right, and they pull against each other:
//  - Take the whole whitespace-delimited token, so a link carrying its own `?`/`&` is never handed over
//    truncated — the same "must travel VERBATIM" rule the harness repeats to the agent.
//  - Identify it by HOST as well as path. `/browse/<id>` is an ordinary path half the web has
//    (`somestore.example/browse/shoes`), and the task summary is model-authored text that routinely
//    names the target site; off our hosts it is a foreign link that must not take the button.
//
// `symphony.wix.com` is the WEB host and `symphony.wix.app` is the app-link domain both phones claim, so
// a `.app` link can be taken by the app rather than the browser (see `native-apps/GOTCHAS.md`). That
// belongs to whoever OPENS the link, not to recognizing it: both spell the same live view.
const URL_TOKEN = /https?:\/\/[^\s<>"']+/gi;
const LIVE_VIEW_HOSTS = new Set(['symphony.wix.com', 'symphony.wix.app']);
const LIVE_VIEW_PATH = '/browse/';

// A URL at the end of a sentence, or inside a markdown link/parenthesis, collects trailing characters
// that are punctuation rather than part of it. A real trailing `?`/`&`/`=` can be significant, so only
// unambiguous enders are trimmed.
const TRAILING_PUNCTUATION = /[).,;:!\]}>'"]+$/;

/**
 * The live view named in `text`, or null.
 *
 * The URL is PARSED rather than string-matched, because the host is the entire reason this link counts as
 * ours: `https://symphony.wix.com@evil.example/browse/x` contains our host and is served by somebody else,
 * and a `contains` test would hand the button to whoever wrote the summary.
 */
export function browseLiveViewUrl(text: string | undefined): string | null {
  if (!text) return null;
  for (const match of text.matchAll(URL_TOKEN)) {
    const url = match[0].replace(TRAILING_PUNCTUATION, '');
    if (isLiveViewUrl(url)) return url;
  }
  return null;
}

/** `/browse/<id>` on one of our hosts. The id is REQUIRED: a bare `/browse` is a page, not a session. */
function isLiveViewUrl(url: string): boolean {
  try {
    const { hostname, pathname } = new URL(url);
    return (
      LIVE_VIEW_HOSTS.has(hostname.toLowerCase().replace(/^www\./, '')) &&
      pathname.startsWith(LIVE_VIEW_PATH) &&
      pathname.length > LIVE_VIEW_PATH.length
    );
  } catch {
    return false;
  }
}

/**
 * The action the task's text says is coming, or null for the default Maestro handoff.
 *
 * The three fields are read in the order the agent is most likely to have written the link: `summary` is
 * where `ManageTask` puts the parked reason verbatim, and the other two are read as a fallback so a link
 * the user can SEE on the screen is never one the button ignores.
 */
export function taskKnownAction(text: TaskActionText): TaskKnownAction | null {
  for (const field of [text.summary, text.currentAction, text.subtitle]) {
    const url = browseLiveViewUrl(field);
    if (url) return { kind: 'browseSession', url };
  }
  return null;
}
